import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { cn } from 'lib/utils';
import { EditorPreset } from '../types';
import { MathExtension, SignedImage } from '../extensions';
import { useSignedImageRehydrate } from '../hooks';
import Toolbar from './Toolbar';

interface RichTextEditorProps extends React.AriaAttributes {
  /** HTML value — what the editor emits and consumes. */
  value: string;
  onChange: (html: string) => void;
  preset?: EditorPreset;
  placeholder?: string;
  /** Optional folder hint for S3 uploads (e.g. `questions/<testId>`). */
  uploadContext?: string;
  disabled?: boolean;
  maxLength?: number;
  className?: string;
  /** Minimum editor body height. Default 120px. Lower for inline/compact uses. */
  minHeight?: number;
}

/** MathLive (~250KB) loads only when a math node renders. Persistence stays in the consuming feature module. */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  preset = 'question',
  placeholder,
  uploadContext,
  disabled,
  maxLength,
  className,
  minHeight = 120,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // codeBlock and code are kept; we drop default heading levels to just 2/3 for exam UX
        heading: { levels: [2, 3] },
      }),
      Underline,
      SignedImage.configure({
        inline: false,
        allowBase64: false,
        resize: {
          enabled: true,
          directions: ['bottom-left', 'bottom-right'],
          minWidth: 80,
          minHeight: 40,
          alwaysPreserveAspectRatio: true,
        },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: false }),
      Placeholder.configure({ placeholder: placeholder || 'Start typing…' }),
      ...(maxLength ? [CharacterCount.configure({ limit: maxLength })] : []),
      MathExtension,
    ],
    content: value,
    editable: !disabled,
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaInvalid ? { 'aria-invalid': String(ariaInvalid) } : {}),
        ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        class: cn(
          'prose prose-sm max-w-none focus:outline-none focus-visible:!outline-none px-3 py-2',
          'dark:prose-invert',
          'prose-table:border prose-table:border-gray-300',
          'prose-td:border prose-td:border-gray-300 prose-td:px-2 prose-td:py-1',
          'prose-th:border prose-th:border-gray-300 prose-th:bg-gray-100 prose-th:px-2 prose-th:py-1',
        ),
        style: `min-height: ${minHeight}px`,
      },
    },
  });

  // Sync external value updates (form reset, server refresh)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  // Refresh signed URLs for any persisted images whenever the value loads
  // (resume editing, form reset). Newly-uploaded images already carry a
  // fresh signed URL from the upload response, so they don't need this pass.
  // Signal includes `!!editor` so the effect fires on the render where the
  // container ref first becomes populated (useEditor returns null on render 1,
  // the wrapper mounts on render 2; without the editor bit, value-unchanged
  // deps would skip the only render where ref.current is set).
  const containerRef = useRef<HTMLDivElement | null>(null);
  useSignedImageRehydrate(containerRef, editor ? value : null);

  if (!editor) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'question-rich-input border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-950 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        disabled && 'opacity-60 pointer-events-none',
        className,
      )}
    >
      <Toolbar editor={editor} preset={preset} uploadContext={uploadContext} />
      <EditorContent editor={editor} />
      {typeof maxLength === 'number' && (
        <div className="px-3 py-1 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800">
          {editor.storage.characterCount?.characters?.() ?? 0} / {maxLength}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
